import { motion } from 'framer-motion';
import { fadeUp, fadeLeft, fadeRight, stagger, viewport } from '../../utils/animations';
import { HiAcademicCap, HiTrophy, HiSparkles, HiCheckBadge, HiArrowTopRightOnSquare } from 'react-icons/hi2';

const DEGREES = [
  {
    institution: 'National University',
    location: 'Bangladesh',
    degree: "Bachelor's of Science — Computer Science",
    period: 'Dec 2016 — Jan 2019',
    color: '#6366f1',
    icon: '🎓',
    badge: null,
  },
  {
    institution: 'Sylhet Science & Technology College',
    location: 'Bangladesh',
    degree: 'Higher Secondary Certificate — Computer Science & IT',
    period: 'Feb 2014 — Oct 2016',
    color: '#8b5cf6',
    icon: '📚',
    badge: null,
  },
  {
    institution: 'Bronx Adult Learning Center',
    location: 'NYC DOE District 79 · Bronx, NY',
    degree: 'GED Preparation',
    period: 'Feb 2026 — Present',
    color: '#22d3ee',
    icon: '✏️',
    badge: 'Current',
  },
];

const CERTS = [
  {
    title: 'Next Level Full Stack Track — Web Development',
    issuer: 'Programming Hero',
    type: 'Professional Certificate',
    icon: '🏆',
    color: '#f59e0b',
    note: 'Completed with high score',
    url: 'https://web.programming-hero.com/verification?validationNumber=PHlevel2-batch1L2B1-01951009',
  },
  {
    title: 'Complete Web Development Course',
    issuer: 'Programming Hero',
    type: 'Certificate',
    icon: '🏅',
    color: '#6366f1',
    note: 'Full-stack track',
    url: 'https://drive.google.com/file/d/1HG9zNVq3CkL5VKuKw_g7xj3sLwQxQNaq/view',
  },
  {
    title: 'Learn Advanced React Redux (RTK Query)',
    issuer: 'Think in a Redux Way',
    type: 'Course Award',
    icon: '⚡',
    color: '#8b5cf6',
    note: 'Course completion award',
    url: 'https://learnwithsumit.com/certificates/verify/LWSCTXN-GPBBVEWW',
  },
];

export default function Education() {
  return (
    <section id="education" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 80% 80%, rgba(139,92,246,0.04) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport} className="mb-16">
          <motion.div variants={fadeUp} className="section-label">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">05 / Education</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
            Academic &amp; <span className="gradient-text">Certifications</span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 xl:gap-16">

          <motion.div
            variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport}
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2.5 mb-8">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-indigo-600/15 border border-indigo-500/25">
                <HiAcademicCap className="w-4 h-4 text-indigo-400" />
              </div>
              <h3 className="font-display font-bold text-xl text-white">Formal Education</h3>
            </motion.div>

            <div className="relative pl-8">
              <motion.div
                className="absolute left-3 top-0 bottom-0 w-px"
                style={{ background: 'linear-gradient(to bottom, #6366f1, #8b5cf6, #22d3ee)', originY: 0 }}
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={viewport}
                transition={{ duration: 1.4, ease: 'easeInOut' }}
              />
              <div className="absolute left-3 top-0 bottom-0 w-px timeline-flow opacity-60" />

              <div className="space-y-5">
                {DEGREES.map((d, i) => (
                  <motion.div
                    key={d.institution}
                    variants={fadeLeft}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                    className={`relative glass rounded-2xl p-5 border transition-all duration-300 ${
                      d.badge ? 'border-cyan-500/30 bg-cyan-500/[0.03]' : 'border-white/[0.06] hover:border-white/15'
                    }`}
                  >
                    <div className="absolute -left-[22px] top-6">
                      <div
                        className="w-3 h-3 rounded-full border-2 border-ink-950 relative z-10"
                        style={{ background: d.color }}
                      />
                      <motion.div
                        className="absolute inset-[-3px] rounded-full"
                        style={{ background: d.color, opacity: 0.2 }}
                        animate={{ scale: [1, 1.8, 1], opacity: [0.2, 0, 0.2] }}
                        transition={{ duration: 3, repeat: Infinity, delay: i * 0.8 }}
                      />
                    </div>

                    {d.badge && (
                      <span
                        className="absolute top-3 right-3 font-mono text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ color: d.color, background: `${d.color}18`, border: `1px solid ${d.color}35` }}
                      >
                        {d.badge}
                      </span>
                    )}

                    <div className="flex items-start gap-3">
                      <span className="text-2xl mt-0.5 flex-shrink-0">{d.icon}</span>
                      <div>
                        <h4 className="font-display font-semibold text-white leading-tight">{d.institution}</h4>
                        <p className="text-sm text-zinc-400 mt-1 leading-snug">{d.degree}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="font-mono text-[10px] text-zinc-600 px-2 py-0.5 rounded-md bg-white/[0.03] border border-white/[0.05]">
                            {d.period}
                          </span>
                          <span className="text-[10px] text-zinc-700">{d.location}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport}
          >
            <motion.div variants={fadeUp} className="flex items-center gap-2.5 mb-8">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/25">
                <HiTrophy className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="font-display font-bold text-xl text-white">Certifications</h3>
            </motion.div>

            <div className="space-y-4">
              {CERTS.map((c, i) => (
                <motion.div
                  key={c.title}
                  variants={fadeRight}
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ duration: 0.25 }}
                  className="group relative glass rounded-2xl p-5 border border-white/[0.06] overflow-hidden"
                >
                  <div
                    className="absolute top-0 right-0 w-28 h-28 rounded-full blur-3xl opacity-0 group-hover:opacity-25 transition-opacity duration-500"
                    style={{ background: c.color, transform: 'translate(30%, -30%)' }}
                  />

                  <motion.div
                    className="absolute top-0 left-0 right-0 h-[1.5px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `linear-gradient(90deg, transparent, ${c.color}, transparent)` }}
                  />

                  <div className="relative flex items-start gap-4">
                    <div
                      className="w-11 h-11 flex items-center justify-center rounded-xl text-xl flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                      style={{ background: `${c.color}15`, border: `1px solid ${c.color}28` }}
                    >
                      {c.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-semibold text-white text-sm leading-snug">{c.title}</h4>
                      <p className="text-xs text-zinc-600 mt-1">{c.issuer}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2.5">
                        <span
                          className="font-mono text-[10px] font-bold px-2.5 py-0.5 rounded-full"
                          style={{ color: c.color, background: `${c.color}14`, border: `1px solid ${c.color}28` }}
                        >
                          {c.type}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-zinc-700">
                          <HiCheckBadge className="w-3 h-3 text-emerald-500" />
                          {c.note}
                        </span>
                      </div>
                      {c.url && (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-medium transition-colors hover:opacity-90"
                          style={{ color: c.color }}
                        >
                          View certificate
                          <HiArrowTopRightOnSquare className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

             {/* <motion.div
              variants={fadeRight}
              className="mt-5 glass rounded-2xl p-5 border border-amber-500/20 bg-amber-500/[0.025]"
            >
              <div className="flex items-start gap-3">
                <HiSparkles className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm text-white">Competitions & Achievements</p>
                  <p className="text-xs text-zinc-600 mt-1.5 leading-relaxed">
                    Participated in multiple coding competitions and hackathons.
                    Consistently achieved top scores on professional certification tracks,
                    demonstrating deep full-stack expertise.
                  </p>
                </div>
              </div>
            </motion.div> */}
          </motion.div> 
          
        </div>
      </div>
    </section>
  );
}
